// src/pages/PanelFirmaAbogado/PanelFirmaAbogado.js
//
// Panel de Firma para abogados apoderados. Separado del Panel de Firma del Fiscal.
// Lista las demandas de Carga Masiva que le fueron asignadas para firmar EN SU TURNO
// dentro de la circulación (Orden 1 principal, luego co-apoderados).
//
// Firma con el mismo servicio local 127.0.0.1:5850 que usan las cartas poder.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Container, Paper, Typography, Button, IconButton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox,
    CircularProgress, LinearProgress, Alert,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VpnKey as TokenIcon,
    CheckCircle as CheckCircleIcon,
    Draw as DrawIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
    cargaMasivaMisDemandasPendientesJWT,
    cargaMasivaDescargarDemandaFirmaBlob,
    cargaMasivaSubirDemandaFirmadaJWT,
} from '../../api/endpoints';
import PdfPreviewDialog from '../../components/CargaMasiva/PdfPreviewDialog';
import useServicioFirma, { fetchFirmaLocal } from '../../hooks/useServicioFirma';

const COLOR = '#37474f';

export default function PanelFirmaAbogado() {
    const { enqueueSnackbar } = useSnackbar();
    const servicio = useServicioFirma();
    const [pendientes, setPendientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [seleccionadas, setSeleccionadas] = useState(new Set());
    const [dialogPin, setDialogPin] = useState(false);
    const [pin, setPin] = useState('');
    const [firmando, setFirmando] = useState(false);
    const [progreso, setProgreso] = useState(null);
    const [previewState, setPreviewState] = useState({ open: false, blob: null, titulo: '' });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await cargaMasivaMisDemandasPendientesJWT();
            if (r?.ok) setPendientes(r.demandas || []);
        } catch {
            enqueueSnackbar('Error al cargar mis demandas', { variant: 'error' });
        } finally { setLoading(false); }
    }, [enqueueSnackbar]);

    useEffect(() => { cargar(); }, [cargar]);

    const toggle = (id) => {
        setSeleccionadas(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const toggleTodas = () => {
        if (seleccionadas.size === pendientes.length) setSeleccionadas(new Set());
        else setSeleccionadas(new Set(pendientes.map(p => p.Id_Documento_Firma)));
    };

    const verPDF = async (doc) => {
        try {
            const blob = await cargaMasivaDescargarDemandaFirmaBlob(doc.Id_Documento_Firma);
            setPreviewState({
                open: true, blob,
                titulo: `Demanda — Lote ${doc.Id_Lote} — Fila ${doc.Fila} — ${doc.Razon_Social || ''}`,
            });
        } catch {
            enqueueSnackbar('Error al cargar PDF', { variant: 'error' });
        }
    };

    const firmarSeleccionadas = async () => {
        if (!pin || seleccionadas.size === 0) return;
        setFirmando(true);
        setProgreso({ done: 0, total: seleccionadas.size, errores: [] });
        const ids = Array.from(seleccionadas);
        let done = 0;
        let corte = null;
        const errores = [];

        for (const idDoc of ids) {
            try {
                const blob = await cargaMasivaDescargarDemandaFirmaBlob(idDoc);
                const res = await fetchFirmaLocal(`/firmar?pin=${encodeURIComponent(pin)}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/pdf' },
                    body: blob,
                });
                if (!res.ok) {
                    const text = await res.text();
                    let errObj;
                    try { errObj = JSON.parse(text); } catch { errObj = { error: text }; }
                    throw new Error(errObj.error || 'Error del token');
                }
                const firmado = await res.blob();
                const r = await cargaMasivaSubirDemandaFirmadaJWT(idDoc, new File([firmado], 'firmado.pdf', { type: 'application/pdf' }));
                if (!r?.ok) throw new Error(r?.mensaje || 'Error al subir firmado');
                done++;
            } catch (err) {
                errores.push({ id: idDoc, error: err.message || String(err) });
                // Si es error de token o el cliente se cerró, cortar todo
                if (err.cortar || err.message?.includes('token') || err.message?.includes('PIN')) {
                    corte = err.message;
                    break;
                }
            }
            setProgreso({ done: done + errores.length, total: ids.length, errores });
        }

        setFirmando(false);
        setDialogPin(false);
        setPin('');
        setSeleccionadas(new Set());
        enqueueSnackbar(
            corte
                ? `Firmadas: ${done} / ${ids.length}. Se cortó: ${corte}`
                : `Firmadas: ${done} / ${ids.length}. Errores: ${errores.length}`,
            { variant: errores.length > 0 ? 'warning' : 'success' }
        );
        servicio.verificar();
        cargar();
    };

    return (
        <Box sx={{ bgcolor: '#eceff1', minHeight: '100vh', py: 3, px: { xs: 2, md: 3 } }}>
            <Container maxWidth="xl" disableGutters>
                {/* Header */}
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: '4px', borderLeft: `4px solid ${COLOR}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DrawIcon sx={{ color: COLOR, fontSize: 36 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.1 }}>
                            Panel de Firma de Demandas
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Demandas de Carga Masiva pendientes de tu firma como apoderado
                        </Typography>
                    </Box>
                    <Tooltip title="Refrescar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
                </Paper>

                {!servicio.listo && pendientes.length > 0 && (
                    <Alert severity={servicio.disponible === null ? 'info' : 'warning'} sx={{ mb: 2, borderRadius: '4px' }}>
                        {servicio.mensaje}
                    </Alert>
                )}

                {/* Barra de acciones */}
                {seleccionadas.size > 0 && (
                    <Paper sx={{ p: 2, mb: 2, borderRadius: '4px', bgcolor: '#fff8e1' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Chip label={`${seleccionadas.size} seleccionadas`} sx={{ bgcolor: COLOR, color: 'white' }} />
                            <Tooltip title={servicio.listo ? '' : servicio.mensaje}>
                                <span>
                                    <Button
                                        variant="contained" startIcon={<TokenIcon />}
                                        onClick={() => setDialogPin(true)} disabled={!servicio.listo}
                                        sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' }, textTransform: 'none', fontWeight: 600 }}>
                                        Firmar {seleccionadas.size} con token USB
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Paper>
                )}

                {/* Tabla */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: COLOR }} /></Box>
                ) : pendientes.length === 0 ? (
                    <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '4px' }}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 1 }} />
                        <Typography variant="h6">No tenés demandas pendientes de firmar</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Cuando se genere una demanda que requiera tu firma, va a aparecer acá.
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small"
                                            checked={pendientes.length > 0 && seleccionadas.size === pendientes.length}
                                            indeterminate={seleccionadas.size > 0 && seleccionadas.size < pendientes.length}
                                            onChange={toggleTodas}
                                        />
                                    </TableCell>
                                    <TableCell>Lote</TableCell>
                                    <TableCell>Fila</TableCell>
                                    <TableCell>Demandado</TableCell>
                                    <TableCell>Certificado</TableCell>
                                    <TableCell align="right">Monto</TableCell>
                                    <TableCell>Carta Poder</TableCell>
                                    <TableCell align="center">Orden firma</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendientes.map(p => {
                                    const prefijo = p.Lote_Modulo === 1 ? 'A' : 'B';
                                    const cp = p.Carta_Poder_Numero
                                        ? `${prefijo}-${String(p.Carta_Poder_Numero).padStart(6, '0')}/${String(p.Carta_Poder_Ano).padStart(2, '0')}`
                                        : '—';
                                    const demandado = p.Razon_Social + (p.Nombre_Demandado ? `, ${p.Nombre_Demandado}` : '');
                                    return (
                                        <TableRow key={p.Id_Documento_Firma} hover selected={seleccionadas.has(p.Id_Documento_Firma)}>
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={seleccionadas.has(p.Id_Documento_Firma)} onChange={() => toggle(p.Id_Documento_Firma)} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.Id_Lote}</TableCell>
                                            <TableCell>{p.Fila}</TableCell>
                                            <TableCell>{demandado.toUpperCase()}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{p.Certificado_Serie}-{p.Certificado_Numero}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                {Number(p.Monto_Total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{cp}</TableCell>
                                            <TableCell align="center">
                                                <Chip size="small" label={p.Orden === 1 ? 'Principal' : `Co-apo #${p.Orden}`}
                                                    sx={p.Orden === 1 ? { bgcolor: COLOR, color: 'white' } : {}}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Ver PDF">
                                                    <IconButton size="small" onClick={() => verPDF(p)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Dialog PIN */}
                <Dialog open={dialogPin} onClose={() => !firmando && setDialogPin(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: '4px' } }}>
                    <DialogTitle sx={{ color: COLOR }}>Firmar {seleccionadas.size} demandas</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Insertá el PIN del token USB. Se firmarán las <strong>{seleccionadas.size}</strong> demandas seleccionadas en batch.
                        </Typography>
                        <TextField
                            type="password" label="PIN del token" autoFocus fullWidth
                            value={pin} onChange={e => setPin(e.target.value)} disabled={firmando}
                        />
                        {progreso && firmando && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption">Firmando {progreso.done} / {progreso.total}...</Typography>
                                <LinearProgress variant="determinate" value={(progreso.done / progreso.total) * 100} sx={{ mt: 1 }} />
                            </Box>
                        )}
                        {!servicio.listo && !firmando && (
                            <Alert severity="warning" sx={{ mt: 2, borderRadius: '4px' }}>{servicio.mensaje}</Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogPin(false)} disabled={firmando}>Cancelar</Button>
                        <Button onClick={firmarSeleccionadas} variant="contained" disabled={!pin || firmando || !servicio.listo}
                            sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}>
                            {firmando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Firmar'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <PdfPreviewDialog
                    open={previewState.open}
                    blob={previewState.blob}
                    titulo={previewState.titulo}
                    onClose={() => setPreviewState({ open: false, blob: null, titulo: '' })}
                />
            </Container>
        </Box>
    );
}
